/**
 * Full in-app notification feed. Marks all unread as read on mount.
 */
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { EmptyState, PageHeader } from '../components/ui';
import { InboxItem, useNotifications } from '../context/NotificationsContext';

const Notifications: React.FC = () => {
  const { fetchInbox, markAllRead, unreadCount } = useNotifications();
  const [items, setItems] = useState<InboxItem[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const data = await fetchInbox({
        unreadOnly: filter === 'unread',
      });
      if (!cancelled) {
        setItems(data.items);
        setCursor(data.nextCursor);
        setLoading(false);
      }
    };
    void load();
    void markAllRead();
    return () => {
      cancelled = true;
    };
  }, [fetchInbox, markAllRead, filter]);

  const loadMore = async () => {
    if (cursor == null || loadingMore) return;
    setLoadingMore(true);
    const data = await fetchInbox({
      unreadOnly: filter === 'unread',
      cursor,
    });
    setItems((prev) => [...prev, ...data.items]);
    setCursor(data.nextCursor);
    setLoadingMore(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        subtitle="A cellar log of recent record changes across this winery."
        icon={FiIcons.FiBell({ size: 20 })}
        actions={
          <div className="inline-flex rounded-full border border-monday-border bg-white p-1 shadow-xs">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full ${
                filter === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'text-monday-text-muted hover:text-monday-text'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilter('unread')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full ${
                filter === 'unread'
                  ? 'bg-primary-500 text-white'
                  : 'text-monday-text-muted hover:text-monday-text'
              }`}
            >
              Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </button>
          </div>
        }
      />

      {loading ? (
        <p className="text-sm text-monday-text-muted">Loading activity…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={FiIcons.FiBell({ size: 28 })}
          title="No notifications"
          message="When someone creates, updates, or deletes a cellar record, it will show up here."
        />
      ) : (
        <div className="bg-[#fbf6ee] rounded-2xl border border-[#e4d4c4] shadow-xs overflow-hidden divide-y divide-[#eadccb]">
          {items.map((item) => (
            <FeedRow key={item.id} item={item} />
          ))}
        </div>
      )}

      {cursor != null && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium rounded-full border border-monday-border bg-white text-monday-text hover:bg-monday-header disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

const FeedRow: React.FC<{ item: InboxItem }> = ({ item }) => {
  const unread = !item.readAt;
  const time = (() => {
    try {
      return formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });
    } catch {
      return '';
    }
  })();

  const inner = (
    <div className={`px-4 py-4 ${unread ? 'bg-[#f7ebe0]' : ''}`}>
      <div className="flex gap-3">
        <span
          className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
            unread ? 'bg-error-500' : 'bg-transparent'
          }`}
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#3b241c]">{item.title}</p>
          {item.body && (
            <p className="mt-1 text-sm text-monday-text-muted">{item.body}</p>
          )}
          <p className="mt-1.5 text-xs text-[#9a7d6c]">{time}</p>
        </div>
      </div>
    </div>
  );

  if (item.link) {
    return (
      <Link to={item.link} className="block hover:bg-[#f6efe4]">
        {inner}
      </Link>
    );
  }
  return inner;
};

export default Notifications;
